import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Loader2, Clock, DollarSign, Briefcase } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06 }
  }
};

const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } }
};

const colors = [
  '#6366f1', '#22c55e', '#a855f7', '#ef4444', '#f59e0b', 
  '#ec4899', '#06b6d4', '#8b5cf6', '#14b8a6', '#f97316'
];

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    duration_minutes: 60,
    price: 0,
    color: '#6366f1'
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const { data } = await axios.get(`${API}/api/services`);
      setServices(data);
    } catch (err) {
      console.error('Error fetching services:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        name: service.name,
        duration_minutes: service.duration_minutes,
        price: service.price,
        color: service.color
      });
    } else {
      setEditingService(null);
      setFormData({ name: '', duration_minutes: 60, price: 0, color: '#6366f1' });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSaving(true);
    try {
      if (editingService) {
        await axios.put(`${API}/api/services/${editingService.id}`, formData);
        setServices(services.map(s => s.id === editingService.id ? { ...s, ...formData } : s));
        toast.success('Servicio actualizado');
      } else {
        const { data } = await axios.post(`${API}/api/services`, formData);
        setServices([data, ...services]);
        toast.success('Servicio creado');
      }
      setShowModal(false);
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/api/services/${deleteId}`);
      setServices(services.filter(s => s.id !== deleteId));
      toast.success('Servicio eliminado');
    } catch (err) {
      toast.error('Error al eliminar');
    } finally {
      setShowDeleteDialog(false);
      setDeleteId(null);
    }
  };

  return (
    <Layout>
      <motion.div 
        data-testid="services-page" 
        className="space-y-8"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Header */}
        <motion.div variants={item} className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-headline text-white">Servicios</h1>
            <p className="text-caption mt-1">{services.length} servicios disponibles</p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            data-testid="add-service-button"
            className="nova-btn nova-btn-primary"
          >
            <Plus className="w-4 h-4" />
            Nuevo Servicio
          </button>
        </motion.div>

        {/* Services Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-40 nova-skeleton rounded-xl" />
            ))}
          </div>
        ) : services.length === 0 ? (
          <motion.div variants={item} className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-5">
              <Briefcase className="w-8 h-8 text-indigo-400" />
            </div>
            <p className="text-sm font-medium text-white mb-1">No hay servicios</p>
            <p className="text-caption">Agrega tu primer servicio para empezar</p>
            <button
              onClick={() => handleOpenModal()}
              className="mt-5 nova-btn nova-btn-secondary"
            >
              Crear servicio
            </button>
          </motion.div>
        ) : (
          <motion.div variants={container} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((service, i) => (
              <motion.div
                key={service.id}
                variants={item}
                className="nova-card nova-card-hover p-6 group"
              >
                <div className="flex items-start justify-between mb-5">
                  <div 
                    className="w-2 h-14 rounded-full"
                    style={{ backgroundColor: service.color, boxShadow: `0 0 16px ${service.color}40` }}
                  />
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleOpenModal(service)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <Edit2 className="w-4 h-4 text-[var(--nova-text-secondary)]" />
                    </button>
                    <button
                      onClick={() => { setDeleteId(service.id); setShowDeleteDialog(true); }}
                      className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
                <h3 className="text-title text-white mb-3">{service.name}</h3>
                <div className="flex items-center gap-5 text-sm text-[var(--nova-text-tertiary)]">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    <span>{service.duration_minutes} min</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <DollarSign className="w-4 h-4" />
                    <span className="font-semibold text-[var(--nova-text)]">${service.price}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Service Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-[var(--nova-surface)] border-[var(--nova-border-strong)] text-[var(--nova-text)] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-title">{editingService ? 'Editar Servicio' : 'Nuevo Servicio'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="service-name-input"
                  className="nova-input"
                  placeholder="Ej: Corte de cabello"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Duración (min)</label>
                  <input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 0 })}
                    className="nova-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Precio ($)</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                    className="nova-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Color</label>
                <div className="flex flex-wrap gap-2">
                  {colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-9 h-9 rounded-xl transition-all ${
                        formData.color === color 
                          ? 'ring-2 ring-white/80 ring-offset-2 ring-offset-[var(--nova-surface)] scale-110' 
                          : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="nova-btn nova-btn-secondary flex-1"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-service-button"
                  className="nova-btn nova-btn-primary flex-1"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Guardar
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirm */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="bg-[var(--nova-surface)] border-[var(--nova-border-strong)]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">¿Eliminar servicio?</AlertDialogTitle>
              <AlertDialogDescription className="text-[var(--nova-text-secondary)]">
                Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="nova-btn nova-btn-secondary">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-500 text-white hover:bg-red-600 rounded-xl">
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </motion.div>
    </Layout>
  );
}
